import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Loader2, Square } from 'lucide-react';
import toast from 'react-hot-toast';
import { waysApi, type WaysRunnerSession } from '../../services/waysApi';
import { userApi, type LiveCheckout } from '../../services/api';
import { liveCheckouts } from '../../services/liveCheckouts';
import { fmtTokens, tokensOf } from './waysTokens';
import { relTime, toDate } from './waysTime';

const POLL_MS = 12_000;
/** States in which a runner holds its item in PMO (a queued or unapproved one has no grant yet). */
const HOLDING = ['starting', 'working', 'waiting-on-reply', 'publishing'];

/** The item as people know it (hs-pmo/ep19#4): ways-api 0.29.2+ sends itemKey ("PO92-237"); the id is
 * the fallback for older records. Linked to the item either way. */
function itemLink(item: WaysRunnerSession['item'], itemKey?: string | null) {
  if (!item && !itemKey) return <span className="text-slate-400">—</span>;
  const id = typeof item === 'string' ? item : item?.id;
  const label = itemKey || (typeof item === 'string' ? item : (item?.display || item?.id || ''));
  const title = typeof item === 'string' || !item ? id : item.title;
  const to = id || itemKey;
  return to ? <Link to={`/item/${to}`} className="text-accent hover:underline" title={title}>{label}</Link>
    : <span>{label}</span>;
}

function lastRequestText(r: WaysRunnerSession['lastRequest']): string {
  if (!r) return '—';
  if (typeof r === 'string') return r;
  return [r.kind, r.id, r.ts && toDate(r.ts)?.toLocaleTimeString()].filter(Boolean).join(' · ');
}

/** A Ways message id ("<channel key>#<seq>") opens its thread; anything else is shown as text. */
function lastRequestLink(r: WaysRunnerSession['lastRequest']) {
  const text = lastRequestText(r);
  const m = typeof r === 'string' ? /^(.+)#(\d+)$/.exec(r) : null;
  return m ? <Link to={`/ways?channel=${encodeURIComponent(m[1])}&seq=${m[2]}`} className="text-accent hover:underline">{text}</Link>
    : <span>{text}</span>;
}

/**
 * Runner sessions live (PO92-225, hs-pmo/requests#4): what each runner is working on, for whom,
 * and what it has used, with a terminate button. Polled; Ways decides who may see which
 * project (Ways admins all, a project's moderators theirs).
 */
export default function WaysRunners({ projectName }: { projectName: (id?: string | null) => string }) {
  const [sessions, setSessions] = useState<WaysRunnerSession[] | null>(null);
  const [at, setAt] = useState('');
  const [error, setError] = useState('');
  const [stopping, setStopping] = useState('');
  /** PMO's runner holds by runner session (hs-pmo/ep19#43): the same data as Active now, so the
   *  two views agree on which runner holds what. null until read, or when PMO could not be read. */
  const [holds, setHolds] = useState<Record<string, LiveCheckout> | null>(null);

  /** Runs that ended in the last day, from their own call (Ways 0.36.12, hs-pmo/ep27 message 109): mixed
   *  into the live list they read as still up. Empty where Ways does not offer them. */
  const [endedRuns, setEndedRuns] = useState<WaysRunnerSession[]>([]);
  /** Names for the "user:<id>" subjects Ways records people by; the id shows until known. */
  const [names, setNames] = useState<Record<string, string>>({});
  const asked = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const r = await waysApi.runnerSessions();
      setSessions(r.sessions || []);
      setAt(String(r.at));
      setError('');
    } catch (e) {
      setError((e as Error).message || 'Could not load runner sessions');
    }
    try {
      const r = await waysApi.runnerSessions(undefined, true);
      setEndedRuns((r.sessions || []).filter((s) => s.alive === false));
    } catch {
      setEndedRuns([]);
    }
    try {
      const live = await liveCheckouts(POLL_MS);
      setHolds(Object.fromEntries(live.items.filter((i) => i.runnerSession).map((i) => [i.runnerSession as string, i])));
    } catch {
      setHolds(null);
    }
  }, []);

  // The plain call lists only live runs (an old Ways sends no `alive`: live too); ended ones come
  // from the include=ended call, newest first as Ways orders them (hs-pmo/ep27 messages 102 and 109).
  const live = (sessions || []).filter((s) => s.alive !== false);
  const ended = endedRuns;

  useEffect(() => {
    // Each subject is asked once; one the PMO does not know keeps showing its id.
    const subs = [...(sessions || []), ...endedRuns].flatMap((s) => [s.requestedBy, s.actsFor])
      .filter((x): x is string => !!x && x.startsWith('user:') && !asked.current.has(x));
    if (!subs.length) return;
    subs.forEach((x) => asked.current.add(x));
    userApi.names([...new Set(subs)])
      .then((r) => setNames((n) => ({ ...n, ...r.names })))
      .catch(() => {});
  }, [sessions, endedRuns]);
  const who = (sub?: string) => (sub ? names[sub] || sub : '');

  // PMO holds an item for a runner session Ways no longer lists as live: a stale hold.
  const orphans = holds && sessions
    ? Object.values(holds).filter((h) => !live.some((s) => s.id === h.runnerSession)) : [];

  useEffect(() => {
    load();
    const t = window.setInterval(() => { if (!document.hidden) load(); }, POLL_MS);
    return () => window.clearInterval(t);
  }, [load]);

  const terminate = async (s: WaysRunnerSession) => {
    const reason = window.prompt(`Terminate runner ${s.id}? Give a reason (it is recorded).`);
    if (!reason?.trim()) return;
    setStopping(s.id);
    try {
      await waysApi.terminateRunner(s.id, reason.trim());
      toast.success('Runner terminated');
      await load();
    } catch (e) {
      toast.error((e as Error).message || 'Could not terminate it', { duration: 8000 });
    } finally {
      setStopping('');
    }
  };

  const row = (s: WaysRunnerSession, alive: boolean) => {
    const local = s.mode === 'local';
    const note = s.waiting || s.reason;
    return (
      <tr key={s.id}>
        <td className="py-1.5 pr-3">{s.projectKey || projectName(s.project)}</td>
        <td className="py-1.5 pr-3">{itemLink(s.item, s.itemKey)}</td>
        <td className="py-1.5 pr-3">
          <div>{s.state}</div>
          {local && (
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              subagent of {s.parentSession || 'a session'}
            </div>
          )}
          {note && (
            <div className={`text-[11px] ${s.waiting ? 'text-amber-700 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400'}`}>
              {note}
            </div>
          )}
        </td>
        <td className="py-1.5 pr-3">{who(s.requestedBy) || '—'}{s.actsFor ? ` · ${who(s.actsFor)}` : ''}</td>
        <td className="py-1.5 pr-3 whitespace-nowrap" title={toDate(s.createdAt)?.toLocaleString()}>
          {relTime(s.createdAt)}
        </td>
        <td className="py-1.5 pr-3 whitespace-nowrap">
          {!alive || local || holds === null ? <span className="text-slate-400">—</span>
            : holds[s.id] ? <span className="text-emerald-600 dark:text-emerald-400" title={`lease ${holds[s.id].checkout.until}`}>held</span>
              : HOLDING.includes(s.state)
                ? <span className="text-amber-600 dark:text-amber-400" title="Working, but PMO has no checkout for this runner">not held</span>
                : <span className="text-slate-400">not yet</span>}
        </td>
        <td className="py-1.5 pr-3 text-right font-mono">{fmtTokens(tokensOf(s.usage))}</td>
        <td className="py-1.5 pr-3">{lastRequestLink(s.lastRequest)}</td>
        <td className="py-1.5">
          {/* Only a live server runner can be stopped; a subagent belongs to its own session. */}
          {alive && !local && !['terminated', 'ended', 'done', 'failed'].includes(s.state) && (
            <button type="button" disabled={stopping === s.id} onClick={() => terminate(s)}
              title="Terminate this runner" aria-label={`Terminate runner ${s.id}`}
              className="p-1 text-slate-400 hover:text-red-500 disabled:opacity-40">
              <Square className="w-3.5 h-3.5" />
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Bot className="w-4 h-4 text-accent" /> Runners
        </h2>
        <span className="text-[11px] text-slate-400">
          {at ? `as of ${toDate(Number(at) || at)?.toLocaleTimeString() ?? '—'} · refreshes every ${POLL_MS / 1000}s` : ''}
        </span>
      </div>
      {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {sessions === null ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : sessions.length === 0 && ended.length === 0 ? (
        <p className="py-10 text-center text-sm text-slate-500">No runner sessions right now, and none ended in the last 24 hours.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-slate-500 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="py-1.5 pr-3 font-medium">Project</th>
                <th className="py-1.5 pr-3 font-medium">Item</th>
                <th className="py-1.5 pr-3 font-medium">State</th>
                <th className="py-1.5 pr-3 font-medium">Requested by · acts for</th>
                <th className="py-1.5 pr-3 font-medium">Started</th>
                <th className="py-1.5 pr-3 font-medium">PMO checkout</th>
                <th className="py-1.5 pr-3 font-medium text-right">Tokens</th>
                <th className="py-1.5 pr-3 font-medium">Last request</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-700 dark:text-slate-200">
              {live.length === 0 && (
                <tr><td colSpan={9} className="py-3 text-center text-slate-500">No live runners right now.</td></tr>
              )}
              {live.map((s) => row(s, true))}
            </tbody>
            {ended.length > 0 && (
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-500 dark:text-slate-400">
                <tr>
                  <th colSpan={9} scope="colgroup" className="pt-4 pb-1.5 text-left font-semibold text-slate-600 dark:text-slate-300">
                    Recently ended <span className="font-normal text-slate-400">· last 24 hours</span>
                  </th>
                </tr>
                {ended.map((s) => row(s, false))}
              </tbody>
            )}
          </table>
        </div>
      )}
      {orphans.length > 0 && (
        <div className="mt-4 text-xs">
          <p className="mb-1 text-amber-700 dark:text-amber-400">
            PMO still holds {orphans.length} item{orphans.length === 1 ? '' : 's'} for runner sessions Ways no longer lists as live:
          </p>
          <ul className="space-y-0.5 text-slate-600 dark:text-slate-300">
            {orphans.map((h) => (
              <li key={h.id}>
                <Link to={`/item/${h.id}`} className="text-accent hover:underline">{h.key || h.id}</Link>
                {' '}{h.title} · runner {h.runnerSession} · lease {relTime(h.checkout.until)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
